import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `/help` — the visible fallback (plan-019 WAPP-HOST; OD-24; design §31.8.9) — `data-screen-label="Help fallback"`.
 *
 * A plain page in the portal's own bundle (not the widget's chunk), outside the shell so a signed-out visitor reaches it
 * too: what the help assistant is, why it may be missing (a browser extension can hide it or refuse its connection), and
 * the feedback and support form posting the same api routes as the widget. The person menu's "Help" item opens it
 * whenever the widget is not mounted or not reachable.
 *
 * Who sends what (design §31.4.1–§31.4.2):
 *
 * - **signed in, inside an organization** — `POST /v1/help/requests`: the reply reaches "Your messages" in the help
 *   panel (and an email copy while mail is on);
 * - **anyone else** (signed out, or signed in with no organization open) — `POST /v1/public/help/requests` with its own
 *   solved challenge; an email address is kept only with the unticked consent box ticked, and without one the page says
 *   before sending that no reply can reach them (OD-7);
 * - **an operator acting for the person** — nothing: help is refused to them (the api answers
 *   `403 help_unavailable_impersonating`), so the page says so and offers no form.
 *
 * "Sent — reference H-…" appears only after the api answered 2xx; a retry of the same text reuses its `Idempotency-Key`,
 * so a double press or a dropped answer never stores the message twice. Nothing here is a fill: the person types and
 * presses Send.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  HELP_CONTACT_CONSENT_REVISION,
  HELP_MESSAGE_CHAR_LIMIT,
  HELP_NOTICE_REVISION,
  isHelpLanguage,
  type HelpLanguage,
} from "@wetalk/domain";
import { Field, Input, Segmented, Textarea } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { authContextQuery } from "../../api/query.js";
import { useGateway } from "../../api/provider.js";
import type { ConsoleGateway } from "../../api/gateway.js";
import type { HelpRequestKindView, HelpRequestSentView } from "../../api/slice/help.js";
import {
  browserAnonymousHelpEnvironment,
  helpStatusQuery,
  helpVisitorKey,
  solvedHelpChallenge,
  type AnonymousHelpEnvironment,
} from "../../help/help-gateway.js";
import { ArrowMark, PublicFrame, PublicKicker, usePublicSiteLink } from "../auth/public-frame.js";
import "../auth/auth.css";
import "./fallback.css";

/** The fallback page's own route id, recorded with the request (0153's `route_id` pattern). */
export const HELP_FALLBACK_ROUTE_ID = "help";
export const HELP_FALLBACK_SCREEN_LABEL = "Help fallback";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** How the page sends: as the signed-in person, or as a visitor with a solved challenge. */
type Sender = "person" | "visitor" | "refused";

interface Draft {
  readonly kind: HelpRequestKindView;
  readonly text: string;
  readonly contactEmail: string;
  readonly consent: boolean;
}

const EMPTY_DRAFT: Draft = { kind: "support", text: "", contactEmail: "", consent: false };

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

export interface HelpFallbackScreenProps {
  /** The browser's crypto and storage for a visitor's request; a suite passes fakes. */
  readonly anonymous?: AnonymousHelpEnvironment;
}

async function sendDraft(
  gateway: ConsoleGateway,
  sender: Exclude<Sender, "refused">,
  draft: Draft,
  language: HelpLanguage,
  consoleBuild: string,
  idempotencyKey: string,
  anonymous: AnonymousHelpEnvironment,
): Promise<HelpRequestSentView> {
  const base = {
    kind: draft.kind,
    text: draft.text.trim(),
    origin: "widget_form" as const,
    routeId: HELP_FALLBACK_ROUTE_ID,
    screenLabel: HELP_FALLBACK_SCREEN_LABEL,
    language,
    consoleBuild,
    noticeRevision: HELP_NOTICE_REVISION,
    conversationId: null,
  };
  if (sender === "person") return gateway.sendHelpRequest(base, idempotencyKey);
  const email = draft.contactEmail.trim();
  const keepsAddress = email !== "" && draft.consent;
  return gateway.sendPublicHelpRequest(
    {
      ...base,
      altcha: await solvedHelpChallenge(gateway, anonymous),
      visitorKey: helpVisitorKey(anonymous),
      contactEmail: keepsAddress ? email : null,
      consentRevision: keepsAddress ? HELP_CONTACT_CONSENT_REVISION : null,
    },
    idempotencyKey,
  );
}

export function HelpFallbackScreen({ anonymous }: HelpFallbackScreenProps = {}): ReactNode {
  const { locale } = usePortalTranslation("help");
  const gateway = useGateway();
  const router = useRouter();
  const site = usePublicSiteLink();
  const current = useQuery(authContextQuery(gateway)).data;
  const status = useQuery(helpStatusQuery(gateway));
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState<{ readonly reference: string; readonly reply: "panel" | "email" | "none" } | null>(null);

  const sender: Sender | null =
    current === undefined
      ? null
      : current === null
        ? "visitor"
        : current.actor.kind !== "self"
          ? "refused"
          : current.kind === "selection_required"
            ? "visitor"
            : "person";

  const send = useMutation({
    mutationFn: (input: { readonly sender: Exclude<Sender, "refused">; readonly draft: Draft; readonly key: string }) => {
      if (!isHelpLanguage(locale)) throw new Error(`The help assistant does not speak ${locale}.`);
      return sendDraft(gateway, input.sender, input.draft, locale, router.options.context.consoleVersion, input.key, anonymous ?? browserAnonymousHelpEnvironment());
    },
    onSuccess: (answer, input) => {
      const email = input.draft.contactEmail.trim();
      setSent({
        reference: answer.reference,
        reply: input.sender === "person" ? "panel" : email !== "" && input.draft.consent ? "email" : "none",
      });
      setDraft(EMPTY_DRAFT);
      setIdempotencyKey(newIdempotencyKey());
    },
    onError: (raised) => {
      setProblem(apiProblemText(raised, portalText("help", "fallback.could_not_send")));
    },
  });

  const change = (next: Partial<Draft>): void => {
    setDraft((previous) => ({ ...previous, ...next }));
    // A changed message is a new message: a retry of the old text no longer answers it.
    if (next.text !== undefined || next.kind !== undefined) setIdempotencyKey(newIdempotencyKey());
    setProblem(null);
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (sender === null || sender === "refused" || send.isPending) return;
    const text = draft.text.trim();
    if (text === "") {
      setProblem(portalText("help", "fallback.text_required"));
      return;
    }
    if (text.length > HELP_MESSAGE_CHAR_LIMIT) {
      setProblem(portalText("help", "fallback.text_too_long", { limit: HELP_MESSAGE_CHAR_LIMIT }));
      return;
    }
    const email = draft.contactEmail.trim();
    if (sender === "visitor" && email !== "" && !EMAIL_SHAPE.test(email)) {
      setProblem(portalText("help", "fallback.email_invalid"));
      return;
    }
    if (sender === "visitor" && email !== "" && !draft.consent) {
      setProblem(portalText("help", "fallback.consent_required"));
      return;
    }
    setProblem(null);
    send.mutate({ sender, draft, key: idempotencyKey });
  };

  const intro = (
    <>
      <PublicKicker>{portalText("help", "fallback.kicker")}</PublicKicker>
      <h1 tabIndex={-1}>{portalText("help", "fallback.title")}</h1>
      <p className="pd-lede">{portalText("help", "fallback.lede")}</p>
      <p className="pd-fine">{portalText("help", "fallback.missing")}</p>
    </>
  );

  if (sender === "refused") {
    return (
      <PublicFrame>
        <div className="pd-flow" data-screen-label={HELP_FALLBACK_SCREEN_LABEL}>
          {intro}
          <p className="pd-lede" role="status">
            {portalText("help", "fallback.impersonating")}
          </p>
        </div>
      </PublicFrame>
    );
  }

  if (status.data !== undefined && !status.data.enabled) {
    return (
      <PublicFrame>
        <div className="pd-flow" data-screen-label={HELP_FALLBACK_SCREEN_LABEL}>
          {intro}
          <p className="pd-lede" role="status">
            {portalText("help", "fallback.unavailable")}
          </p>
        </div>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame>
      <div className="pd-flow" data-screen-label={HELP_FALLBACK_SCREEN_LABEL}>
        {intro}
        {sent === null ? null : (
          <div className="c-wtq-sent" role="status">
            <p>{portalText("help", "fallback.sent", { reference: sent.reference })}</p>
            <p className="pd-fine">
              {sent.reply === "panel"
                ? portalText("help", "fallback.reply_panel")
                : sent.reply === "email"
                  ? portalText("help", "fallback.reply_email")
                  : portalText("help", "fallback.reply_none")}
            </p>
          </div>
        )}
        <form onSubmit={onSubmit} noValidate aria-label={portalText("help", "fallback.form_label")} className="pd-form">
          <Segmented<HelpRequestKindView>
            name="kind"
            legend={portalText("help", "fallback.kind_legend")}
            value={draft.kind}
            options={[
              { value: "support", label: portalText("help", "fallback.kind_support") },
              { value: "feedback", label: portalText("help", "fallback.kind_feedback") },
            ]}
            onChange={(kind) => {
              change({ kind });
            }}
          />
          <Field
            label={portalText("help", "fallback.text_label")}
            description={portalText("help", "fallback.text_count", { count: draft.text.trim().length, limit: HELP_MESSAGE_CHAR_LIMIT })}
          >
            {(control) => (
              <Textarea
                {...control}
                name="text"
                rows={6}
                maxLength={HELP_MESSAGE_CHAR_LIMIT}
                value={draft.text}
                onChange={(event) => {
                  change({ text: event.currentTarget.value });
                }}
              />
            )}
          </Field>
          {sender === "visitor" ? (
            <>
              <Field label={portalText("help", "fallback.email_label")} description={portalText("help", "fallback.email_hint")}>
                {(control) => (
                  <Input
                    {...control}
                    name="contactEmail"
                    type="email"
                    autoComplete="email"
                    value={draft.contactEmail}
                    onChange={(event) => {
                      change({ contactEmail: event.currentTarget.value });
                    }}
                  />
                )}
              </Field>
              <label className="c-wtq-consent">
                <input
                  type="checkbox"
                  name="consent"
                  checked={draft.consent}
                  onChange={(event) => {
                    change({ consent: event.currentTarget.checked });
                  }}
                />
                <span>{portalText("help", "fallback.consent")}</span>
              </label>
              {draft.contactEmail.trim() === "" ? <p className="pd-fine">{portalText("help", "fallback.no_reply_without_email")}</p> : null}
            </>
          ) : null}
          <p className="pd-fine">
            {portalText("help", "fallback.notice")}{" "}
            <a href={site("/privacy/")} target="_blank" rel="noreferrer">
              {portalText("help", "fallback.privacy_link")}
            </a>
          </p>
          {problem === null ? null : (
            <p className="wt-field-error" role="alert">
              {problem}
            </p>
          )}
          <button type="submit" className="pd-cta" disabled={sender === null || send.isPending}>
            {send.isPending ? portalText("help", "fallback.sending") : portalText("help", "fallback.send")}
            <ArrowMark />
          </button>
        </form>
      </div>
    </PublicFrame>
  );
}
